import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  X, 
  Trash2, 
  ArrowRight, 
  CheckCircle, 
  ShieldCheck, 
  Sparkles,
  CreditCard,
  Smartphone
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Button from './Button';
import './CartDrawer.css';

export default function CartDrawer({ onOpenAuth }) {
  const { 
    items, 
    itemCount, 
    subtotalCents, 
    formattedSubtotal, 
    currency,
    setCurrency,
    isCartOpen, 
    setIsCartOpen, 
    removeItem, 
    checkout, 
    loading 
  } = useCart();
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [success, setSuccess] = useState(false);

  if (!isCartOpen) return null;

  const handleCheckout = async () => {
    if (!user) {
      setIsCartOpen(false);
      onOpenAuth?.();
      return;
    }

    const ok = await checkout(paymentMethod);
    if (ok) {
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setIsCartOpen(false);
        navigate('/library');
      }, 1500);
    }
  };

  return (
    <div className="cart-backdrop" onClick={() => setIsCartOpen(false)}>
      <div className="cart-drawer glass-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-title">
            <ShoppingBag size={20} className="text-primary" />
            <span>Shopping Cart</span>
            <span className="cart-badge-count">{itemCount}</span>
          </div>
          <button className="cart-close-btn" onClick={() => setIsCartOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Success Modal View */}
        {success ? (
          <div className="cart-success-view">
            <div className="cart-success-icon">
              <CheckCircle size={48} />
            </div>
            <h3>Order Complete!</h3>
            <p>Your source files, PSDs, and Figma kits have been unlocked.</p>
            <span className="redirecting-note">Redirecting to your Library...</span>
          </div>
        ) : (
          <>
            {/* Items List */}
            <div className="cart-items-container">
              {items.length === 0 ? (
                <div className="cart-empty-state">
                  <div className="empty-cart-icon">
                    <ShoppingBag size={42} />
                  </div>
                  <h4>Your cart is empty</h4>
                  <p>Browse the marketplace for high-res templates, vector packs, and design systems.</p>
                  <Button variant="secondary" onClick={() => { setIsCartOpen(false); navigate('/browse'); }}>
                    Explore Marketplace
                  </Button>
                </div>
              ) : (
                items.map((item) => {
                  const id = item.template_id || item.id;
                  const isFree = item.is_free === 1 || item.is_free === true || !item.price_cents || isNaN(Number(item.price_cents)) || Number(item.price_cents) <= 0;
                  const priceCents = Number(item.price_cents) || 0;
                  const price = isFree 
                    ? 'Free' 
                    : currency === 'GHS'
                      ? `GH₵ ${((priceCents / 100) * 15).toFixed(2)}`
                      : `$${(priceCents / 100).toFixed(2)}`;

                  return (
                    <div key={id} className="cart-item-card">
                      <img 
                        src={item.preview_url || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&q=80'} 
                        alt={item.title} 
                        className="cart-item-img"
                      />
                      <div className="cart-item-details">
                        <h4 className="cart-item-title">{item.title}</h4>
                        <span className="cart-item-designer">by @{item.designer_name || 'Creator'}</span>
                        <div className="cart-item-bottom">
                          <span className={`cart-item-price ${isFree ? 'text-green' : ''}`}>
                            {price}
                          </span>
                          <button 
                            className="cart-remove-btn" 
                            onClick={() => removeItem(id)}
                            title="Remove item"
                          >
                            <Trash2 size={14} />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer / Checkout */}
            {items.length > 0 && (
              <div className="cart-footer">
                {/* Currency Switcher */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Currency</span>
                  <div style={{ display: 'flex', background: 'var(--bg-base)', borderRadius: '10px', padding: '2px', border: '1px solid var(--border-subtle)' }}>
                    <button
                      type="button"
                      onClick={() => setCurrency('GHS')}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: currency === 'GHS' ? 'var(--primary)' : 'transparent',
                        color: currency === 'GHS' ? '#FFFFFF' : 'var(--text-secondary)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      GH₵ GHS
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrency('USD')}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: currency === 'USD' ? 'var(--primary)' : 'transparent',
                        color: currency === 'USD' ? '#FFFFFF' : 'var(--text-secondary)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      $ USD
                    </button>
                  </div>
                </div>

                {/* Payment method selector */}
                <div className="payment-method-selector">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span className="pay-method-label">Payment Channel:</span>
                    <span style={{ fontSize: '0.6875rem', color: 'var(--accent)', fontWeight: 700 }}>⚡ Paystack Secure</span>
                  </div>
                  <div className="pay-method-options">
                    <button 
                      type="button" 
                      className={`pay-btn ${paymentMethod === 'momo' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('momo')}
                    >
                      <Smartphone size={14} />
                      <span>Mobile Money</span>
                    </button>
                    <button 
                      type="button" 
                      className={`pay-btn ${paymentMethod === 'card' ? 'active' : ''}`}
                      onClick={() => setPaymentMethod('card')}
                    >
                      <CreditCard size={14} />
                      <span>Card / Visa</span>
                    </button>
                  </div>
                  {paymentMethod === 'momo' && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.6875rem', background: '#FFCC0020', color: '#FFB800', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>MTN MoMo</span>
                      <span style={{ fontSize: '0.6875rem', background: '#E6000020', color: '#FF4D4D', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>Telecel Cash</span>
                      <span style={{ fontSize: '0.6875rem', background: '#0055FF20', color: '#3388FF', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>AT Money</span>
                    </div>
                  )}
                </div>

                <div className="cart-summary-line">
                  <span>Subtotal</span>
                  <span className="cart-subtotal-val">{formattedSubtotal}</span>
                </div>
                <div className="cart-summary-line">
                  <span>Instant Source File License</span>
                  <span className="text-green">Included</span>
                </div>

                <div className="cart-guarantee">
                  <ShieldCheck size={14} />
                  <span>Secured by Paystack • Instant Vault Delivery</span>
                </div>

                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full checkout-action-btn"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  {loading ? 'Connecting Paystack...' : (
                    <>
                      <span>{user ? `Pay with Paystack • ${formattedSubtotal}` : 'Sign In to Complete Purchase'}</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
